import { TranslateModule } from '@ngx-translate/core';
import { IFlatModel } from '@services/api/flat/flat-model.interface';
import { LogService } from '@services/util/log.service';
import { SelectItemLoaderService, UiSelectItem } from '@services/util/select-item-loader.service';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Observable, skip, Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ParkingRequestFilter } from '../../models/parking-request-filter.model';

@Component({
  selector: 'app-parking-request-filters',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    DatePickerModule,
    SelectModule,
    RadioButtonModule,
    CardModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    AccordionModule,
    SelectButtonModule
  ],
  template: `
    <div class="mb-4">
      <p-accordion
        [multiple]="false"
        [activeIndex]="isExpanded ? 0 : -1"
        (onClose)="isExpanded = false"
        (onOpen)="isExpanded = true"
      >
        <p-accordion-panel value="0">
          <p-accordion-header>
            <div class="flex items-center gap-2">
              <i class="pi pi-filter"></i>
              <span>{{ 'filters.title' | translate }}</span>
              @if (hasActiveFilters()) {
                <span class="ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-white">{{
                  countActiveFilters()
                }}</span>
              }
            </div>
          </p-accordion-header>
          <p-accordion-content>
            <form
              [formGroup]="filterForm"
              class="flex flex-col gap-3"
            >
              <!-- Start Date Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'filters.startDate' | translate }}</h5>
                <div class="flex flex-col gap-4 sm:flex-row">
                  <div class="w-full sm:w-1/3 lg:w-1/4">
                    <p-select
                      formControlName="startDateOperator"
                      [options]="dateOperatorOptions"
                      optionLabel="label"
                      optionValue="value"
                      styleClass="w-full"
                      appendTo="body"
                    >
                      <ng-template
                        pTemplate="selectedItem"
                        let-item
                      >
                        {{ item.label | translate }}
                      </ng-template>
                      <ng-template
                        pTemplate="item"
                        let-option
                      >
                        {{ option.label | translate }}
                      </ng-template>
                    </p-select>
                  </div>
                  <div class="w-full sm:w-2/3 lg:w-1/3">
                    <p-datepicker
                      formControlName="startDate"
                      styleClass="w-full"
                      [showIcon]="true"
                      [dateFormat]="dateFormat"
                      [placeholder]="'filters.datePlaceholder' | translate"
                    >
                    </p-datepicker>
                  </div>
                </div>
              </div>

              <!-- End Date Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'filters.endDate' | translate }}</h5>
                <div class="flex flex-col gap-3 sm:flex-row">
                  <div class="w-full sm:w-1/3 lg:w-1/4">
                    <p-select
                      formControlName="endDateOperator"
                      [options]="dateOperatorOptions"
                      optionLabel="label"
                      optionValue="value"
                      styleClass="w-full"
                      appendTo="body"
                    >
                      <ng-template
                        pTemplate="selectedItem"
                        let-item
                      >
                        {{ item.label | translate }}
                      </ng-template>
                      <ng-template
                        pTemplate="item"
                        let-option
                      >
                        {{ option.label | translate }}
                      </ng-template>
                    </p-select>
                  </div>
                  <div class="w-full sm:w-2/3 lg:w-1/3">
                    <p-datepicker
                      formControlName="endDate"
                      styleClass="w-full"
                      [showIcon]="true"
                      [dateFormat]="dateFormat"
                      [placeholder]="'filters.datePlaceholder' | translate"
                    >
                    </p-datepicker>
                  </div>
                </div>
              </div>

              <!-- Parking Place Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'parking.filters.parkingPlace' | translate }}</h5>
                <div class="w-full md:w-2/3 lg:w-1/2">
                  <p-select
                    formControlName="parkingPlaceId"
                    [options]="(parkingPlaceItems$ | async) || []"
                    optionLabel="explicitTitle"
                    optionValue="model.id"
                    styleClass="w-full"
                    [placeholder]="'parking.filters.parkingPlacePlaceholder' | translate"
                    [showClear]="true"
                    [loading]="parkingPlaceLoading"
                    [emptyMessage]="'parking.filters.noParkingPlaces' | translate"
                    appendTo="body"
                  >
                  </p-select>
                </div>
              </div>

              <!-- License Plate Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'parking.filters.licensePlate' | translate }}</h5>
                <div>
                  <p-inputGroup class="w-full md:w-2/3 lg:w-1/2">
                    <p-inputGroupAddon>
                      <i class="pi pi-search"></i>
                    </p-inputGroupAddon>
                    <input
                      type="text"
                      pInputText
                      formControlName="licensePlate"
                      class="w-full"
                      [placeholder]="'filters.searchPlaceholder' | translate"
                    />
                    @if (filterForm.get('licensePlate')?.value) {
                      <p-inputGroupAddon>
                        <button
                          type="button"
                          class="p-inputgroup-addon cursor-pointer"
                          (click)="clearFilterField('licensePlate')"
                        >
                          <i class="pi pi-times"></i>
                        </button>
                      </p-inputGroupAddon>
                    }
                  </p-inputGroup>
                </div>
              </div>

              <!-- Booking Name Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'parking.filters.bookingName' | translate }}</h5>
                <div>
                  <p-inputGroup class="w-full md:w-2/3 lg:w-1/2">
                    <p-inputGroupAddon>
                      <i class="pi pi-calendar"></i>
                    </p-inputGroupAddon>
                    <input
                      type="text"
                      pInputText
                      formControlName="bookingName"
                      class="w-full"
                      [placeholder]="'parking.filters.bookingNamePlaceholder' | translate"
                    />
                    @if (filterForm.get('bookingName')?.value) {
                      <p-inputGroupAddon>
                        <button
                          type="button"
                          class="p-inputgroup-addon cursor-pointer"
                          (click)="clearFilterField('bookingName')"
                        >
                          <i class="pi pi-times"></i>
                        </button>
                      </p-inputGroupAddon>
                    }
                  </p-inputGroup>
                </div>
              </div>

              <!-- Payment Status Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'filters.paymentStatus' | translate }}</h5>
                <div class="flex flex-col gap-2">
                  <p-selectbutton
                    [options]="paymentStatusOptions"
                    formControlName="paymentStatus"
                    [multiple]="false"
                    optionLabel="label"
                    optionValue="value"
                  >
                    <ng-template
                      let-item
                      pTemplate="item"
                    >
                      {{ item.label | translate }}
                    </ng-template>
                    <ng-template
                      let-item
                      pTemplate="selectedItem"
                    >
                      {{ item.label | translate }}
                    </ng-template>
                  </p-selectbutton>
                </div>
              </div>

              <!-- Filter Buttons -->
              <div class="flex justify-end">
                <div class="flex gap-3">
                  <button
                    pButton
                    type="button"
                    [label]="'common.reset' | translate"
                    class="p-button-outlined"
                    (click)="resetFilters()"
                  ></button>
                  <button
                    pButton
                    type="button"
                    [label]="'common.apply' | translate"
                    class="p-button-primary"
                    (click)="applyFilters()"
                  ></button>
                </div>
              </div>
            </form>
          </p-accordion-content>
        </p-accordion-panel>
      </p-accordion>
    </div>
  `
})
export class ParkingRequestFiltersComponent implements OnInit, OnDestroy {
  /**
   * Active filter to populate form values for editing
   */
  @Input() activeFilter: ParkingRequestFilter | undefined;

  /**
   * Event emitted when filters are applied
   */
  @Output() filterChange = new EventEmitter<ParkingRequestFilter>();

  /**
   * Date format string
   */
  dateFormat = 'yy-mm-dd';

  /**
   * Filter form group
   */
  filterForm!: FormGroup;

  /**
   * Options for date filter operators
   */
  dateOperatorOptions = [
    { label: 'filters.exact', value: 'exact' },
    { label: 'filters.before', value: 'before' },
    { label: 'filters.after', value: 'after' },
    { label: 'filters.strictlyBefore', value: 'strictly_before' },
    { label: 'filters.strictlyAfter', value: 'strictly_after' }
  ];

  /**
   * Options for payment status filter
   */
  paymentStatusOptions = [
    { label: 'filters.all', value: null },
    { label: 'common.yes', value: 1 },
    { label: 'common.no', value: 0 }
  ];

  /**
   * Controls the expanded/collapsed state of the filters accordion
   */
  isExpanded = false;

  private fb = inject(FormBuilder);
  private log = inject(LogService);
  private selectItemLoader = inject(SelectItemLoaderService);
  private destroy$ = new Subject<void>();

  parkingPlaceItems$: Observable<UiSelectItem<IFlatModel>[]>;
  parkingPlaceLoading = true;

  constructor() {
    this.parkingPlaceItems$ = this.selectItemLoader.getParkingPlaceSelectItems();
    
    this.parkingPlaceItems$
      .pipe(
        skip(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.parkingPlaceLoading = false;
      });
  }

  /**
   * Initialize form
   */
  ngOnInit(): void {
    this.initForm();
    this.populateForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize filter form
   */
  private initForm(): void {
    this.filterForm = this.fb.group({
      startDate: [null],
      startDateOperator: ['exact'],
      endDate: [null],
      endDateOperator: ['exact'],
      parkingPlaceId: [null],
      licensePlate: [null],
      paymentStatus: [null],
      bookingName: [null]
    });
  }

  /**
   * Populate form with active filter values
   */
  private populateForm(): void {
    if (this.activeFilter) {
      if (this.activeFilter.startDate) {
        this.filterForm.patchValue({
          startDate: this.activeFilter.startDate.date,
          startDateOperator: this.activeFilter.startDate.operator
        });
      }

      if (this.activeFilter.endDate) {
        this.filterForm.patchValue({
          endDate: this.activeFilter.endDate.date,
          endDateOperator: this.activeFilter.endDate.operator
        });
      }

      this.filterForm.patchValue({
        parkingPlaceId: this.activeFilter.parkingPlaceId,
        licensePlate: this.activeFilter.licensePlate,
        paymentStatus: this.activeFilter.paid,
        bookingName: this.activeFilter['booking.name']
      });
    }
  }

  /**
   * Apply filter values and emit filter change event
   */
  applyFilters(): void {
    const formValue = this.filterForm.value;
    const filter: ParkingRequestFilter = {};

    if (formValue.startDate) {
      filter.startDate = {
        date: formValue.startDate,
        operator: formValue.startDateOperator
      };
    }

    if (formValue.endDate) {
      filter.endDate = {
        date: formValue.endDate,
        operator: formValue.endDateOperator
      };
    }

    if (formValue.parkingPlaceId) {
      filter.parkingPlaceId = formValue.parkingPlaceId;
    }

    if (formValue.licensePlate) {
      filter.licensePlate = formValue.licensePlate;
    }

    if (formValue.bookingName) {
      filter['booking.name'] = formValue.bookingName;
    }

    // Boolean filters on the API use 0 or 1 instead of true/false
    if (formValue.paymentStatus !== null) {
      filter.paid = formValue.paymentStatus;
    }

    this.log.debug('Applied parking request filters:', filter);
    this.filterChange.emit(filter);
  }

  /**
   * Reset filter values and emit empty filter
   */
  resetFilters(): void {
    this.filterForm.reset({
      startDateOperator: 'exact',
      endDateOperator: 'exact',
      paymentStatus: null,
      bookingName: null
    });
    this.filterChange.emit({});
  }

  /**
   * Clear a specific filter field
   * @param fieldName The name of the field to clear
   */
  clearFilterField(fieldName: string): void {
    this.filterForm.get(fieldName)?.reset();
  }

  /**
   * Checks if any filters are currently active
   * @returns true if any filter has a value, false otherwise
   */
  hasActiveFilters(): boolean {
    const formValue = this.filterForm?.value;
    if (!formValue) return false;

    return !!(
      formValue.startDate ||
      formValue.endDate ||
      formValue.parkingPlaceId ||
      formValue.licensePlate ||
      formValue.bookingName ||
      (formValue.paymentStatus !== null && formValue.paymentStatus !== undefined)
    );
  }

  /**
   * Counts the number of active filters
   * @returns number of active filters
   */
  countActiveFilters(): number {
    const formValue = this.filterForm?.value;
    if (!formValue) return 0;

    let count = 0;
    if (formValue.startDate) count++;
    if (formValue.endDate) count++;
    if (formValue.parkingPlaceId) count++;
    if (formValue.licensePlate) count++;
    if (formValue.bookingName) count++;
    if (formValue.paymentStatus !== null && formValue.paymentStatus !== undefined) count++;

    return count;
  }
}
